import React, { useEffect, useState } from 'react';
import { api } from '../api';
import AgentPairingModal from './AgentPairingModal';

const PROVIDERS = [
  { id: 'bedrock',   label: 'AWS Bedrock (recommended)',
    helper: 'Authenticated via the EC2 instance role — no key needed. Common models: anthropic.claude-sonnet-4-6-20250929-v1:0' },
  { id: 'anthropic', label: 'Anthropic API',
    helper: 'Requires an ANTHROPIC API key in the Secrets section below.' },
  { id: 'ollama',    label: 'Local Ollama',
    helper: 'Uses the Ollama server on the same host. Examples: gemma3:27b, qwen2.5:32b' },
];

const SECRET_PROVIDERS = [
  { id: 'apollo',    label: 'Apollo.io',  field: 'API key' },
  { id: 'hunter',    label: 'Hunter.io',  field: 'API key' },
  { id: 'snov',      label: 'Snov.io',    fields: ['client_id', 'client_secret'] },
  // Anthropic key is now also needed when Research provider = 'anthropic'
  // (Bedrock doesn't support the web_search tool). Always show the row so
  // admins can configure it without first switching the general LLM provider.
  { id: 'anthropic', label: 'Anthropic',  field: 'API key' },
  { id: 'hubspot',   label: 'HubSpot',    field: 'API key' },
];

const TABS = [
  { id: 'llm',      label: 'LLM & secrets' },
  { id: 'users',    label: 'Users' },
  { id: 'agents',   label: 'Connected agents' },
  { id: 'budgets',  label: 'Budgets' },
  { id: 'products', label: 'Products / ICP' },
  { id: 'stages',   label: 'Deal stages' },
  { id: 'reasons',  label: 'Lost reasons' },
  // 'programs' (Nurture programs) tab retired — the `nurture` deal stage
  // replaces the heavy nurture pipeline.
];

/**
 * Drop this into Settings as a new tab "Admin settings" (visible to role=admin only).
 * Contains four sub-tabs:
 *   - LLM & secrets — provider/model + Apollo/Hunter/Snov/Anthropic keys
 *   - Users — list, promote/demote, deactivate
 *   - Connected agents — desktop agent status per rep
 *   - Budgets — daily $ cap per provider (Apollo, Hunter, Snov, LLM)
 */
export default function AdminSettingsPanel() {
  const [tab, setTab] = useState('llm');
  return (
    // App shell wraps panels in `overflow-hidden`, so each panel has to own
    // its own scroll. `h-full overflow-y-auto` keeps the content scrollable
    // when the tab body exceeds the viewport (e.g. all the secret rows).
    <div className="h-full overflow-y-auto p-8 max-w-4xl scrollable-content">
      <h1 className="text-xl font-semibold text-white mb-1">Admin settings</h1>
      <div className="text-slate-500 text-xs mb-6">Internal-only configuration.</div>

      <div className="flex gap-1 border-b border-slate-700 mb-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              'px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors ' +
              (tab === t.id
                ? 'border-indigo-500 text-white'
                : 'border-transparent text-slate-400 hover:text-slate-200')
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'llm'      && <LLMAndSecrets />}
      {tab === 'users'    && <UsersTab />}
      {tab === 'agents'   && <AgentsTab />}
      {tab === 'budgets'  && <BudgetsTab />}
      {tab === 'products' && <ProductsTab />}
      {tab === 'stages'   && <DealStagesTab />}
      {tab === 'reasons'  && <LostReasonsTab />}
    </div>
  );
}


// Suggested Bedrock + Anthropic-direct model ids for the research dropdown.
// Keep these in sync with anthropic / Bedrock catalog when new models drop.
const RESEARCH_MODELS = {
  bedrock: [
    { id: 'us.anthropic.claude-sonnet-4-5-20250929-v1:0', label: 'Sonnet 4.5 (recommended)', cost: '~$0.10–0.13 / company' },
    { id: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',  label: 'Haiku 4.5 (cheap & fast)',  cost: '~$0.04–0.05 / company' },
    { id: 'us.anthropic.claude-opus-4-20250514-v1:0',     label: 'Opus 4 (overkill)',         cost: '~$0.40–0.50 / company' },
  ],
  anthropic: [
    { id: 'claude-sonnet-4-5-20250929', label: 'Sonnet 4.5 (recommended)', cost: '~$0.10–0.13 / company' },
    { id: 'claude-haiku-4-5-20251001',  label: 'Haiku 4.5 (cheap & fast)',  cost: '~$0.04–0.05 / company' },
    { id: 'claude-opus-4-20250514',     label: 'Opus 4 (overkill)',         cost: '~$0.40–0.50 / company' },
  ],
};


function LLMAndSecrets() {
  const [settings, setSettings] = useState(null);
  const [llmProvider, setLLMProvider] = useState('bedrock');
  const [llmModel, setLLMModel] = useState('anthropic.claude-sonnet-4-6-20250929-v1:0');
  // Research-specific knobs (separate from ad-hoc LLM settings).
  const [researchProvider, setResearchProvider] = useState('bedrock');
  const [researchModel,    setResearchModel]    = useState(RESEARCH_MODELS.bedrock[0].id);
  const [researchMaxSearches, setResearchMaxSearches] = useState(6);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [researchSaving, setResearchSaving] = useState(false);
  const [researchSavedAt, setResearchSavedAt] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setLoadError(null);
      const s = await api.getAdminSettings();
      setSettings(s);
      setLLMProvider(s.llm.provider);
      setLLMModel(s.llm.model);
      if (s.research) {
        setResearchProvider(s.research.provider);
        setResearchModel(s.research.model);
        setResearchMaxSearches(s.research.max_searches || 6);
      }
    } catch (e) {
      setLoadError(String(e.message || e));
    }
  }

  async function saveResearch() {
    setResearchSaving(true);
    setError(null);
    try {
      await api.updateResearchSettings({
        provider: researchProvider,
        model: researchModel,
        max_searches: Number(researchMaxSearches) || 6,
      });
      setResearchSavedAt(Date.now());
      load();
    } catch (e) {
      setError('Failed to save research settings: ' + String(e.message || e));
    } finally { setResearchSaving(false); }
  }

  async function saveLLM() {
    setSaving(true);
    setError(null);
    try {
      await api.updateLLMSettings({ provider: llmProvider, model: llmModel });
      setSavedAt(Date.now());
      load();
    } catch (e) {
      setError('Failed to save LLM settings: ' + String(e.message || e));
    } finally { setSaving(false); }
  }

  if (loadError) {
    return (
      <div className="text-sm text-red-300 bg-red-900/40 border border-red-700 rounded p-3">
        Failed to load settings: {loadError}
        <button onClick={load} className="ml-3 text-slate-300 underline hover:text-white">Retry</button>
      </div>
    );
  }
  if (!settings) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {error && (
        <div className="mb-4 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>
      )}
      {/* LLM section */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-3">LLM</h2>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-5">
          <label className="block mb-4">
            <div className="text-xs text-slate-400 mb-1">Provider</div>
            <select
              value={llmProvider}
              onChange={(e) => setLLMProvider(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <div className="text-xs text-slate-500 mt-1">
              {PROVIDERS.find((p) => p.id === llmProvider)?.helper}
            </div>
          </label>
          <label className="block mb-4">
            <div className="text-xs text-slate-400 mb-1">Model</div>
            <input
              value={llmModel}
              onChange={(e) => setLLMModel(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm font-mono"
            />
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={saveLLM}
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {savedAt && (
              <span className="text-xs text-emerald-400">Saved {new Date(savedAt).toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      </section>

      {/* Research model section — separate from the general LLM settings
          because research uses Claude's web_search tool which is only on
          Anthropic/Bedrock. */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-3">Company research</h2>
        <div className="text-xs text-slate-500 mb-3">
          Powers <span className="font-mono text-slate-300">/api/companies/research</span> — Claude does its own web searches and returns a structured profile. Bedrock uses the EC2 IAM role (no key needed); Anthropic direct requires a key in the API Credentials section below.
        </div>
        <div className="bg-slate-800 border border-slate-700 rounded-lg p-5 space-y-4">
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Provider</div>
            <select
              value={researchProvider}
              onChange={(e) => {
                const next = e.target.value;
                setResearchProvider(next);
                // Reset model to first option for the new provider so we don't leave
                // a Bedrock-flavored id under Anthropic-direct (or vice versa).
                setResearchModel(RESEARCH_MODELS[next][0].id);
              }}
              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              <option value="bedrock">AWS Bedrock (recommended — IAM auth)</option>
              <option value="anthropic">Anthropic API (needs key)</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Model</div>
            <select
              value={researchModel}
              onChange={(e) => setResearchModel(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm"
            >
              {RESEARCH_MODELS[researchProvider].map((m) => (
                <option key={m.id} value={m.id}>{m.label} — {m.cost}</option>
              ))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1 font-mono">{researchModel}</div>
          </label>
          <label className="block">
            <div className="text-xs text-slate-400 mb-1">Max web searches per company</div>
            <input
              type="number"
              min={1}
              max={15}
              value={researchMaxSearches}
              onChange={(e) => setResearchMaxSearches(Number(e.target.value) || 6)}
              className="w-24 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm tabular-nums"
            />
            <span className="text-[11px] text-slate-500 ml-2">Lower = cheaper, less thorough. 6 is a good default.</span>
          </label>
          <div className="flex items-center gap-3">
            <button
              onClick={saveResearch}
              disabled={researchSaving}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-medium disabled:opacity-50"
            >
              {researchSaving ? 'Saving…' : 'Save'}
            </button>
            {researchSavedAt && (
              <span className="text-xs text-emerald-400">Saved {new Date(researchSavedAt).toLocaleTimeString()}</span>
            )}
          </div>
        </div>
      </section>

      {/* Secrets section */}
      <section>
        <h2 className="text-sm font-semibold text-slate-300 uppercase tracking-wide mb-3">API Credentials</h2>
        <div className="text-xs text-slate-500 mb-3">
          Saved to AWS Secrets Manager. Existing values are write-only — paste a new value to update; leave blank to keep the current one.
        </div>
        <div className="space-y-3">
          {SECRET_PROVIDERS
            .filter((p) => !p.condition || p.condition(settings))
            .map((p) => (
              <SecretRow key={p.id} provider={p} status={settings.secrets.find((s) => s.provider === p.id)} onSaved={load} />
            ))}
        </div>
      </section>
    </div>
  );
}

function SecretRow({ provider, status, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const payload = provider.fields
        ? { client_id: clientId, client_secret: clientSecret }
        : value;
      await api.setSecret(provider.id, payload);
      // Only clear + close on success — a failed save used to close the row
      // and wipe the typed value as if it had saved.
      setEditing(false);
      setValue(''); setClientId(''); setClientSecret('');
      onSaved?.();
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  }

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-4 flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-slate-200">{provider.label}</div>
        <div className="text-xs text-slate-500 mt-0.5">
          {status?.configured ? <span className="text-emerald-400">✓ Configured</span> : <span className="text-amber-400">Not configured</span>}
        </div>
      </div>
      {!editing ? (
        <button
          onClick={() => setEditing(true)}
          className="px-3 py-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded"
        >
          {status?.configured ? 'Update' : 'Set'}
        </button>
      ) : (
        <div className="flex flex-col items-end gap-1">
        {error && <div className="text-[11px] text-red-300">{error}</div>}
        <div className="flex items-center gap-2">
          {provider.fields ? (
            <>
              <input
                placeholder="client_id"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm w-40"
              />
              <input
                type="password"
                placeholder="client_secret"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm w-40"
              />
            </>
          ) : (
            <input
              type="password"
              placeholder="paste new key…"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm w-64"
            />
          )}
          <button onClick={save} disabled={busy} className="px-3 py-1 text-xs bg-indigo-600 rounded">
            {busy ? '…' : 'Save'}
          </button>
          <button onClick={() => { setEditing(false); setError(null); }} className="px-3 py-1 text-xs text-slate-400">
            Cancel
          </button>
        </div>
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Users tab
// ---------------------------------------------------------------------------
function UsersTab() {
  const [users, setUsers] = useState(null);
  const [busy, setBusy] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setError(null);
      setUsers(await api.listUsers());
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  async function patch(id, body) {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.updateUser(id, body);
      await load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  }

  async function disable(id) {
    if (!confirm('Disable this user? Their sessions and agent tokens will be revoked.')) return;
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await api.disableUser(id);
      await load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  }

  if (!users) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {error && (
        <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>
      )}
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2 px-4">Email</th>
              <th className="text-left py-2 px-4">Name</th>
              <th className="text-left py-2 px-4">Role</th>
              <th className="text-left py-2 px-4">Active</th>
              <th className="text-right py-2 px-4"></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-700">
                <td className="py-2 px-4 text-slate-200">{u.email}</td>
                <td className="py-2 px-4">
                  <input
                    defaultValue={u.name}
                    disabled={busy[u.id]}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== u.name) patch(u.id, { name: v });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') e.target.blur();
                    }}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200 w-44"
                  />
                </td>
                <td className="py-2 px-4">
                  <select
                    value={u.role}
                    disabled={busy[u.id]}
                    onChange={(e) => patch(u.id, { role: e.target.value })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs"
                  >
                    <option value="member">member</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td className="py-2 px-4">
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={u.active}
                      disabled={busy[u.id]}
                      onChange={(e) => patch(u.id, { active: e.target.checked })}
                    />
                    {u.active ? <span className="text-emerald-400">active</span> : <span className="text-slate-500">disabled</span>}
                  </label>
                </td>
                <td className="py-2 px-4 text-right">
                  {u.active && (
                    <button
                      onClick={() => disable(u.id)}
                      disabled={busy[u.id]}
                      className="text-xs text-red-400 hover:text-red-300"
                    >
                      Disable
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 mt-3">
        New users are created on first Auth0 login. Promote anyone to admin by changing their role.
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Connected agents tab — desktop scraper status per rep
// ---------------------------------------------------------------------------
function AgentsTab() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [showPair, setShowPair] = useState(false);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);  // refresh every 30s
    return () => clearInterval(t);
  }, []);

  async function load() {
    try {
      setError(null);
      setRows(await api.listAdminAgents());
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  function badge(mins) {
    if (mins === null || mins === undefined) return <span className="text-slate-500">never</span>;
    if (mins < 15)  return <span className="text-emerald-400">live</span>;
    if (mins < 120) return <span className="text-amber-400">{mins}m ago</span>;
    return <span className="text-red-400">{Math.floor(mins / 60)}h ago</span>;
  }

  if (!rows) return <div className="text-slate-400">Loading…</div>;

  const pairButton = (
    <button
      onClick={() => setShowPair(true)}
      className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded"
    >
      + Pair my agent
    </button>
  );

  if (rows.length === 0) {
    return (
      <div>
        <div className="mb-3 flex justify-end">{pairButton}</div>
        <div className="text-sm text-slate-400 bg-slate-800 border border-slate-700 rounded-lg p-6">
          No paired agents yet. Click <span className="text-slate-200">Pair my agent</span> above to generate a code, then enter it in the Siphon Agent desktop app.
        </div>
        {showPair && <AgentPairingModal onClose={() => { setShowPair(false); load(); }} />}
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>
      )}
      <div className="mb-3 flex justify-end">{pairButton}</div>
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2 px-4">Rep</th>
              <th className="text-left py-2 px-4">Version</th>
              <th className="text-left py-2 px-4">Last seen</th>
              <th className="text-right py-2 px-4">Leads 24h</th>
              <th className="text-right py-2 px-4">Jobs 24h</th>
              <th className="text-left py-2 px-4">IP</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.token_id} className="border-t border-slate-700">
                <td className="py-2 px-4 text-slate-200">
                  {r.user_name} <span className="text-slate-500 text-xs">{r.user_email}</span>
                </td>
                <td className="py-2 px-4 text-slate-400 font-mono text-xs">{r.agent_version || '—'}</td>
                <td className="py-2 px-4">{badge(r.minutes_since_seen)}</td>
                <td className="py-2 px-4 text-right tabular-nums text-slate-300">{r.leads_pushed_24h}</td>
                <td className="py-2 px-4 text-right tabular-nums text-slate-300">{r.jobs_completed_24h}</td>
                <td className="py-2 px-4 text-slate-500 font-mono text-xs">{r.last_ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-slate-500 mt-3">Auto-refreshes every 30 seconds.</div>
      {showPair && <AgentPairingModal onClose={() => { setShowPair(false); load(); }} />}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Budgets tab — daily $ cap per provider
// ---------------------------------------------------------------------------
function BudgetsTab() {
  const [state, setState] = useState(null);
  const [caps, setCaps] = useState({});      // provider -> string in the input
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      setError(null);
      const s = await api.getBudgets();
      setState(s);
      const next = {};
      for (const p of s.providers) next[p.provider] = p.daily_cap ? String(p.daily_cap) : '';
      setCaps(next);
    } catch (e) {
      setError(String(e.message || e));
    }
  }

  async function save() {
    setSaving(true);
    try {
      const payload = {};
      for (const [p, v] of Object.entries(caps)) {
        const n = Number(v);
        if (Number.isFinite(n) && n > 0) payload[p] = n;
      }
      await api.updateBudgets(payload);
      setSavedAt(Date.now());
      await load();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  }

  if (!state) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {error && (
        <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>
      )}
      <div className="text-xs text-slate-500 mb-3">
        Daily USD cap per provider. Leave blank for "no cap". Spend resets at midnight UTC.
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2 px-4">Provider</th>
              <th className="text-left py-2 px-4">Spent today</th>
              <th className="text-left py-2 px-4">Unit cost</th>
              <th className="text-left py-2 px-4">Daily cap ($)</th>
            </tr>
          </thead>
          <tbody>
            {state.providers.map((p) => {
              const overCap = p.daily_cap > 0 && p.spent_today >= p.daily_cap;
              return (
                <tr key={p.provider} className="border-t border-slate-700">
                  <td className="py-2 px-4 text-slate-200 font-medium">{p.provider}</td>
                  <td className={'py-2 px-4 tabular-nums ' + (overCap ? 'text-red-400' : 'text-slate-300')}>
                    ${p.spent_today.toFixed(2)}
                  </td>
                  <td className="py-2 px-4 text-slate-500 tabular-nums">${p.unit_cost}</td>
                  <td className="py-2 px-4">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={caps[p.provider] ?? ''}
                      onChange={(e) => setCaps({ ...caps, [p.provider]: e.target.value })}
                      placeholder="no cap"
                      className="w-28 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm tabular-nums"
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded text-sm font-medium disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save caps'}
        </button>
        {savedAt && (
          <span className="text-xs text-emerald-400">Saved {new Date(savedAt).toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Deal stages tab
// ---------------------------------------------------------------------------
function DealStagesTab() {
  const [stages, setStages] = useState(null);
  const [error, setError] = useState(null);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => { load(); }, []);
  async function load() {
    try { setError(null); setStages(await api.listDealStages()); }
    catch (e) { setError(e.message); }
  }
  async function patch(id, body) {
    try { await api.updateDealStage(id, body); await load(); }
    catch (e) { setError(e.message); }
  }
  async function add() {
    if (!newLabel.trim()) return;
    try { await api.createDealStage({ label: newLabel.trim() }); setNewLabel(''); await load(); }
    catch (e) { setError(e.message); }
  }
  async function remove(id) {
    if (!confirm('Delete this stage? It can only be deleted if no deals reference it.')) return;
    try { await api.deleteDealStage(id); await load(); }
    catch (e) { setError(e.message); }
  }

  if (!stages) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {error && <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>}
      <div className="text-xs text-slate-500 mb-3">
        Labels, colors, probabilities, and order can be edited. The internal id is fixed — it's referenced by deals + stage history.
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2 px-3">Label</th>
              <th className="text-left py-2 px-3">Color</th>
              <th className="text-left py-2 px-3">Prob %</th>
              <th className="text-left py-2 px-3">Order</th>
              <th className="text-left py-2 px-3">Flags</th>
              <th className="text-right py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {stages.map(s => (
              <tr key={s.id} className="border-t border-slate-700">
                <td className="py-2 px-3">
                  <input
                    defaultValue={s.label}
                    onBlur={(e) => e.target.value !== s.label && patch(s.id, { label: e.target.value })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100 w-44"
                  />
                  <div className="text-[10px] text-slate-500 font-mono">{s.id}</div>
                </td>
                <td className="py-2 px-3">
                  <input
                    type="color"
                    defaultValue={s.color}
                    onBlur={(e) => e.target.value !== s.color && patch(s.id, { color: e.target.value })}
                    className="bg-transparent border border-slate-700 rounded w-12 h-7 cursor-pointer"
                  />
                </td>
                <td className="py-2 px-3">
                  <input
                    type="number" min="0" max="100"
                    defaultValue={s.probability}
                    onBlur={(e) => Number(e.target.value) !== s.probability && patch(s.id, { probability: Number(e.target.value) })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm w-16 tabular-nums"
                  />
                </td>
                <td className="py-2 px-3">
                  <input
                    type="number"
                    defaultValue={s.ord}
                    onBlur={(e) => Number(e.target.value) !== s.ord && patch(s.id, { ord: Number(e.target.value) })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm w-14 tabular-nums"
                  />
                </td>
                <td className="py-2 px-3 text-xs space-x-2">
                  <label className="inline-flex items-center gap-1">
                    <input type="checkbox" checked={s.in_funnel} onChange={(e) => patch(s.id, { in_funnel: e.target.checked })} />
                    funnel
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input type="checkbox" checked={s.is_won} onChange={(e) => patch(s.id, { is_won: e.target.checked })} />
                    won
                  </label>
                  <label className="inline-flex items-center gap-1">
                    <input type="checkbox" checked={s.is_lost} onChange={(e) => patch(s.id, { is_lost: e.target.checked })} />
                    lost
                  </label>
                </td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => remove(s.id)} className="text-xs text-slate-500 hover:text-red-400">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex gap-2 items-center">
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder="New stage label (e.g. Pilot)"
          className="flex-1 max-w-xs bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        />
        <button onClick={add} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-sm">
          Add stage
        </button>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Lost reasons tab
// ---------------------------------------------------------------------------
function LostReasonsTab() {
  const [reasons, setReasons]   = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, []);
  async function load() {
    try { setError(null); setReasons(await api.listLostReasons()); }
    catch (e) { setError(e.message); }
  }
  async function patch(id, body) {
    try { await api.updateLostReason(id, body); await load(); }
    catch (e) { setError(e.message); }
  }
  async function add() {
    if (!newLabel.trim()) return;
    try { await api.createLostReason({ label: newLabel.trim() }); setNewLabel(''); await load(); }
    catch (e) { setError(e.message); }
  }
  async function remove(id) {
    if (!confirm('Delete this reason?')) return;
    try { await api.deleteLostReason(id); await load(); }
    catch (e) { setError(e.message); }
  }

  if (!reasons) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {error && <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>}
      <div className="text-xs text-slate-500 mb-3">
        Reps pick one of these whenever they close a deal as lost. To keep a deal alive instead, move it into the <span className="text-sky-400 font-medium">Nurture</span> stage rather than closing as lost.
      </div>
      <div className="bg-slate-800 border border-slate-700 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left py-2 px-3">Label</th>
              <th className="text-right py-2 px-3"></th>
            </tr>
          </thead>
          <tbody>
            {reasons.map(r => (
              <tr key={r.id} className="border-t border-slate-700">
                <td className="py-2 px-3">
                  <input
                    defaultValue={r.label}
                    onBlur={(e) => e.target.value !== r.label && patch(r.id, { label: e.target.value })}
                    className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100 w-64"
                  />
                  <div className="text-[10px] text-slate-500 font-mono">{r.id}</div>
                </td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => remove(r.id)} className="text-xs text-slate-500 hover:text-red-400">
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-4 flex gap-2 items-center">
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          placeholder='New reason (e.g. "Lost to incumbent vendor")'
          className="flex-1 max-w-md bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        />
        <button onClick={add} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded text-sm">
          Add reason
        </button>
      </div>
    </div>
  );
}


// NurtureProgramsTab was removed when the heavy nurture pipeline was
// retired. Deals that need long-cycle warm-touch now sit in the `nurture`
// deal stage with tasks driving cadence.


// ---------------------------------------------------------------------------
// Products / ICP tab — edit the products list that drives company scoring
// ---------------------------------------------------------------------------
function ProductsTab() {
  const [products, setProducts] = useState(null);
  const [editing, setEditing]   = useState(null);   // product being edited, or 'new'
  const [error, setError]       = useState(null);

  useEffect(() => { load(); }, []);
  async function load() {
    try {
      setError(null);
      setProducts(await api.fetch('/api/products'));
    } catch (e) { setError(e.message); }
  }

  async function remove(id) {
    if (!confirm('Delete this product? Scores against it will be removed too.')) return;
    try {
      await api.fetch(`/api/products/${id}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(e.message); }
  }

  if (!products) return <div className="text-slate-400">Loading…</div>;

  return (
    <div>
      {error && <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{error}</div>}
      <div className="text-xs text-slate-500 mb-3">
        Products power the per-deal ICP scoring + the Claude research prompts. Be specific in `target_titles`, `target_industries`, `value_props`, and `custom_criteria`.
      </div>

      <div className="flex justify-end mb-3">
        <button
          onClick={() => setEditing('new')}
          className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded"
        >
          + Add product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="text-sm text-slate-400 bg-slate-800 border border-slate-700 rounded-lg p-6 text-center">
          No products yet. Add one to start scoring leads.
        </div>
      ) : (
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.id} className="bg-slate-800 border border-slate-700 rounded-lg p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-100">{p.name}</div>
                  {p.description && <div className="text-xs text-slate-400 mt-0.5 line-clamp-2">{p.description}</div>}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(p.target_industries || []).slice(0, 5).map((t) => (
                      <span key={t} className="text-[10px] bg-slate-700 text-slate-300 px-2 py-0.5 rounded">{t}</span>
                    ))}
                    {(p.target_titles || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-[10px] bg-indigo-500/20 text-indigo-300 px-2 py-0.5 rounded">{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => setEditing(p)} className="text-xs text-slate-400 hover:text-slate-200">Edit</button>
                  <button onClick={() => remove(p.id)} className="text-xs text-slate-500 hover:text-red-400">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProductEditor
          product={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const isNew = !product;
  const [form, setForm] = useState({
    name:                 product?.name || '',
    description:          product?.description || '',
    value_props:          product?.value_props || '',
    target_titles:        (product?.target_titles || []).join(', '),
    target_industries:    (product?.target_industries || []).join(', '),
    target_company_sizes: (product?.target_company_sizes || []).join(', '),
    custom_criteria:      product?.custom_criteria || '',
    url:                  product?.url || '',
    category:             product?.category || '',
    use_cases:            product?.use_cases || '',
    differentiators:      product?.differentiators || '',
    competitors:          (product?.competitors || []).join(', '),
    disqualifiers:        product?.disqualifiers || '',
    keywords:             (product?.keywords || []).join(', '),
    buyer_personas:       product?.buyer_personas || '',
    price_range:          product?.price_range || '',
    typical_deal_size:    product?.typical_deal_size || '',
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState(null);

  function parseList(s) {
    return (s || '').split(',').map(t => t.trim()).filter(Boolean);
  }

  async function save() {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setSaving(true); setErr(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        value_props: form.value_props,
        target_titles:        parseList(form.target_titles),
        target_industries:    parseList(form.target_industries),
        target_company_sizes: parseList(form.target_company_sizes),
        custom_criteria: form.custom_criteria,
        url: form.url,
        category: form.category,
        use_cases: form.use_cases,
        differentiators: form.differentiators,
        competitors: parseList(form.competitors),
        disqualifiers: form.disqualifiers,
        keywords: parseList(form.keywords),
        buyer_personas: form.buyer_personas,
        price_range: form.price_range,
        typical_deal_size: form.typical_deal_size,
      };
      if (isNew) {
        await api.fetch('/api/products', { method: 'POST', body: JSON.stringify(payload) });
      } else {
        await api.fetch(`/api/products/${product.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      }
      onSaved?.();
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const F = (key, label, opts = {}) => (
    <div className={opts.full ? 'col-span-2' : ''}>
      <label className="text-[11px] text-slate-400">{label}</label>
      {opts.textarea ? (
        <textarea
          rows={opts.rows || 2}
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          placeholder={opts.placeholder}
          className="w-full mt-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        />
      ) : (
        <input
          value={form[key]}
          onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          placeholder={opts.placeholder}
          className="w-full mt-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-100"
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-slate-100 mb-1">{isNew ? 'New product' : `Edit "${product.name}"`}</h3>
        <p className="text-xs text-slate-500 mb-4">
          Comma-separated lists for titles/industries/sizes/competitors/keywords. The richer these fields, the better Claude scores fit.
        </p>

        {err && <div className="mb-3 p-2 bg-red-900/40 border border-red-700 rounded text-xs text-red-200">{err}</div>}

        <div className="grid grid-cols-2 gap-3">
          {F('name', 'Name *', { placeholder: 'Alpha Threat Detection' })}
          {F('category', 'Category', { placeholder: 'SOC platform' })}
          {F('description', 'Description', { full: true, textarea: true, rows: 2, placeholder: 'One-line product summary.' })}
          {F('value_props', 'Value props', { full: true, textarea: true, rows: 3, placeholder: 'What customers care about.' })}
          {F('target_titles', 'Target titles', { placeholder: 'CISO, Head of Security, VP of Engineering' })}
          {F('target_industries', 'Target industries', { placeholder: 'Cybersecurity, FinTech, SaaS' })}
          {F('target_company_sizes', 'Target sizes', { placeholder: '51-200, 201-1000' })}
          {F('buyer_personas', 'Buyer personas', { placeholder: 'Technical buyer + procurement' })}
          {F('use_cases', 'Use cases', { full: true, textarea: true, rows: 2 })}
          {F('differentiators', 'Differentiators', { full: true, textarea: true, rows: 2 })}
          {F('competitors', 'Competitors', { placeholder: 'CrowdStrike, SentinelOne' })}
          {F('keywords', 'Keywords', { placeholder: 'XDR, EDR, MDR' })}
          {F('disqualifiers', 'Disqualifiers', { placeholder: 'Sub-50 headcount, no security team' })}
          {F('custom_criteria', 'Custom ICP criteria', { full: true, textarea: true, rows: 3 })}
          {F('price_range', 'Price range', { placeholder: '$30-150k ARR' })}
          {F('typical_deal_size', 'Typical deal size', { placeholder: '$60k' })}
          {F('url', 'Product URL', { full: true, placeholder: 'https://alphalevel.ai/products/threat-detection' })}
        </div>

        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 px-3 py-2 text-sm text-slate-300 border border-slate-700 rounded hover:bg-slate-800">
            Cancel
          </button>
          <button onClick={save} disabled={saving} className="flex-1 px-3 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
